<!-- Polaris (fase 5.1): the head of the contacts list on the kit's PageHeader shape (cards/PageHeader.vue: same
     row, same classes) with the view's count in a Badge beside the title (the 3.1 "Conversations 8" pattern), which
     PageHeader's string title cannot take. Search in the kit's InputGroup, same rule as the woot (server search, the
     page debounces 300 ms). Fase 5.2: the actions are the kit's ghost icon buttons with an accessible name
     (filter, save/delete segment, sort, "⋮" with import/export), then "New contact" (primary, opens the drawer,
     R2-5-PC-5) and "Message" (ComposeConversation, R2-5-PC-6 = A). The woot popovers behind them stay. -->
<script setup>
import { useI18n } from 'vue-i18n';
import { useNumberFormatter } from 'shared/composables/useNumberFormatter';
import {
  Add16,
  Edit16,
  Filter16,
  Save16,
  Search16,
  SendAlt16,
  TrashCan16,
} from '@carbon/icons-vue';
import { Button } from 'dashboard/components-next/polaris/ui/button';
import { Badge } from 'dashboard/components-next/polaris/ui/badge';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from 'dashboard/components-next/polaris/ui/input-group';
import ContactSortMenu from './components/ContactSortMenu.vue';
import ContactMoreActions from './components/ContactMoreActions.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';

defineProps({
  showSearch: { type: Boolean, default: true },
  searchValue: { type: String, default: '' },
  headerTitle: { type: String, required: true },
  count: { type: Number, default: null },
  buttonLabel: { type: String, default: '' },
  activeSort: { type: String, default: 'last_activity_at' },
  activeOrdering: { type: String, default: '' },
  isSegmentsView: { type: Boolean, default: false },
  hasActiveFilters: { type: Boolean, default: false },
  isLabelView: { type: Boolean, default: false },
  isActiveView: { type: Boolean, default: false },
});

const emit = defineEmits([
  'search',
  'filter',
  'update:sort',
  'add',
  'import',
  'export',
  'createSegment',
  'deleteSegment',
]);

const { t } = useI18n();
// The viewer's locale (pt_BR → pt-BR); vue-i18n's n() hands 'pt_BR' to Intl and throws.
const { formatFullNumber: n } = useNumberFormatter();
</script>

<template>
  <header
    class="relative z-20 flex min-h-16 shrink-0 flex-wrap items-center gap-x-3 gap-y-2 border-0 border-b border-solid border-polaris-border px-6 py-3"
  >
    <div class="flex min-w-0 flex-1 basis-40 items-center gap-2">
      <h1
        class="min-w-0 truncate type-polaris-page-title text-polaris-foreground"
        :title="headerTitle"
      >
        {{ headerTitle }}
      </h1>
      <Badge
        v-if="count"
        variant="secondary"
        class="shrink-0"
        data-contacts-count
      >
        {{ n(count) }}
      </Badge>
    </div>
    <div class="flex min-w-0 flex-wrap items-center gap-2">
      <InputGroup v-if="showSearch" class="h-8 w-full sm:w-56">
        <InputGroupInput
          :model-value="searchValue"
          type="search"
          :aria-label="t('CONTACTS_POLARIS.HEADER.SEARCH_LABEL')"
          :placeholder="t('CONTACTS_LAYOUT.HEADER.SEARCH_PLACEHOLDER')"
          @update:model-value="value => emit('search', value)"
        />
        <InputGroupAddon><Search16 aria-hidden="true" /></InputGroupAddon>
      </InputGroup>
      <div class="flex items-center gap-1">
        <div v-if="!isLabelView && !isActiveView" class="relative">
          <Button
            id="toggleContactsFilterButton"
            variant="ghost"
            size="icon-sm"
            class="relative"
            :aria-label="
              isSegmentsView
                ? t('CONTACTS_LAYOUT.FILTER.EDIT_SEGMENT')
                : t('CONTACTS_LAYOUT.FILTER.TITLE')
            "
            :title="
              isSegmentsView
                ? t('CONTACTS_LAYOUT.FILTER.EDIT_SEGMENT')
                : t('CONTACTS_LAYOUT.FILTER.TITLE')
            "
            @click="emit('filter')"
          >
            <Edit16 v-if="isSegmentsView" aria-hidden="true" />
            <Filter16 v-else aria-hidden="true" />
            <span
              v-if="hasActiveFilters && !isSegmentsView"
              class="absolute end-1 top-1 size-2 rounded-full bg-polaris-primary"
              data-contacts-filter-dot
            />
          </Button>
          <slot name="filter" />
        </div>
        <Button
          v-if="
            hasActiveFilters && !isSegmentsView && !isLabelView && !isActiveView
          "
          variant="ghost"
          size="icon-sm"
          :aria-label="t('CONTACTS_POLARIS.HEADER.SAVE_SEGMENT')"
          :title="t('CONTACTS_POLARIS.HEADER.SAVE_SEGMENT')"
          @click="emit('createSegment')"
        >
          <Save16 aria-hidden="true" />
        </Button>
        <Button
          v-if="isSegmentsView && !isLabelView && !isActiveView"
          variant="ghost"
          size="icon-sm"
          :aria-label="t('CONTACTS_POLARIS.HEADER.DELETE_SEGMENT')"
          :title="t('CONTACTS_POLARIS.HEADER.DELETE_SEGMENT')"
          @click="emit('deleteSegment')"
        >
          <TrashCan16 aria-hidden="true" />
        </Button>
        <ContactSortMenu
          :active-sort="activeSort"
          :active-ordering="activeOrdering"
          @update:sort="emit('update:sort', $event)"
        />
        <ContactMoreActions @import="emit('import')" @export="emit('export')" />
      </div>
      <div class="flex items-center gap-2">
        <Button size="sm" data-contacts-new @click="emit('add')">
          <Add16 aria-hidden="true" />
          {{ t('CONTACTS_LAYOUT.HEADER.ACTIONS.CONTACT_CREATION.ADD_CONTACT') }}
        </Button>
        <ComposeConversation>
          <template #trigger>
            <Button variant="outline" size="sm">
              <SendAlt16 aria-hidden="true" />
              {{ buttonLabel }}
            </Button>
          </template>
        </ComposeConversation>
      </div>
    </div>
  </header>
</template>
